import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/MathGraph.css';

/**
 * MathGraph — Componente de gráfica matemática interactiva (Desmos)
 *
 * Uso:
 *   <MathGraph config={pregunta.grafica} />
 *
 * Formato del campo `grafica` en Firestore:
 * {
 *   expresiones: ["y=x^2-4x+3", "(2,-1)"],  // array de expresiones Desmos
 *   ventana: { xMin: -2, xMax: 6, yMin: -3, yMax: 8 },  // opcional
 *   etiquetas: ["f(x) = x² - 4x + 3"],  // etiquetas opcionales
 *   altura: 320  // altura en px, default 300
 * }
 */
const DESMOS_SCRIPT_URL = 'https://www.desmos.com/api/v1.10/calculator.js?apiKey=dcb31709b452b1cf9dc26972add0fda6';

let desmosScriptLoaded = false;
let desmosScriptLoading = false;
const desmosReadyCallbacks = [];

function loadDesmosScript(onReady) {
  if (desmosScriptLoaded && window.Desmos) {
    onReady();
    return;
  }
  desmosReadyCallbacks.push(onReady);
  if (desmosScriptLoading) return;

  desmosScriptLoading = true;
  const script = document.createElement('script');
  script.src = DESMOS_SCRIPT_URL;
  script.async = true;
  script.onload = () => {
    desmosScriptLoaded = true;
    desmosScriptLoading = false;
    desmosReadyCallbacks.forEach(cb => cb());
    desmosReadyCallbacks.length = 0;
  };
  script.onerror = () => {
    desmosScriptLoading = false;
    desmosReadyCallbacks.length = 0;
  };
  document.head.appendChild(script);
}

// Paleta de colores Tactical Navy para curvas Desmos
const CURVE_COLORS = ['#c5a059', '#4fc3f7', '#a5d6a7', '#ef9a9a', '#ce93d8', '#ffcc02'];

const MathGraph = ({ config }) => {
  const containerRef = useRef(null);
  const calculatorRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  const initDesmos = useCallback(() => {
    if (!window.Desmos || !containerRef.current || calculatorRef.current) return;

    try {
      const ventana = config?.ventana || {};
      const calculator = window.Desmos.GraphingCalculator(containerRef.current, {
        keypad: false,
        expressions: false,
        settingsMenu: false,
        zoomButtons: true,
        lockViewport: false,
        border: false,
        backgroundColor: 'rgba(10,26,42,0)',
        textColor: '#b8c4d0',
        gridColor: 'rgba(197,160,89,0.15)',
        xAxisColor: 'rgba(197,160,89,0.4)',
        yAxisColor: 'rgba(197,160,89,0.4)',
      });

      // Aplicar ventana si fue especificada
      if (ventana.xMin !== undefined) {
        calculator.setMathBounds({
          left: ventana.xMin,
          right: ventana.xMax,
          bottom: ventana.yMin,
          top: ventana.yMax,
        });
      }

      // Agregar expresiones
      const expresiones = config?.expresiones || [];
      expresiones.forEach((expr, i) => {
        // Detectar si es un punto: "(x,y)"
        const puntoMatch = expr.match(/^\((-?[\d.]+),\s*(-?[\d.]+)\)$/);
        if (puntoMatch) {
          calculator.setExpression({
            id: `p${i}`,
            latex: `(${puntoMatch[1]}, ${puntoMatch[2]})`,
            color: CURVE_COLORS[i % CURVE_COLORS.length],
            pointStyle: 'OPEN',
            pointSize: 14,
            label: config?.etiquetas?.[i] || expr,
            showLabel: true,
          });
        } else {
          calculator.setExpression({
            id: `e${i}`,
            latex: expr,
            color: CURVE_COLORS[i % CURVE_COLORS.length],
            lineWidth: 2.5,
            label: config?.etiquetas?.[i] || '',
            showLabel: !!config?.etiquetas?.[i],
          });
        }
      });

      calculatorRef.current = calculator;
      setStatus('ready');
    } catch (err) {
      console.error('[MathGraph] Error al inicializar Desmos:', err);
      setStatus('error');
    }
  }, [config]);

  useEffect(() => {
    if (!config) return;
    loadDesmosScript(() => initDesmos());

    return () => {
      if (calculatorRef.current) {
        calculatorRef.current.destroy();
        calculatorRef.current = null;
      }
    };
  }, [config, initDesmos]);

  // Re-inicializar si cambia la configuración (nueva pregunta)
  useEffect(() => {
    if (calculatorRef.current) {
      calculatorRef.current.destroy();
      calculatorRef.current = null;
    }
    if (config && window.Desmos) {
      initDesmos();
    }
  }, [JSON.stringify(config?.expresiones)]);

  if (!config) return null;

  const altura = config.altura || 300;

  return (
    <div className="math-graph-wrapper" aria-label="Gráfica matemática interactiva">
      <div className="math-graph-header">
        <svg className="math-graph-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
        </svg>
        <span>GRÁFICA INTERACTIVA</span>
        <span className="math-graph-hint">Arrastra para mover · Pellizca para zoom</span>
      </div>
      <div
        className={`math-graph-canvas ${status}`}
        ref={containerRef}
        style={{ height: altura }}
      >
        {status === 'loading' && (
          <div className="math-graph-loading">
            <div className="math-graph-spinner" />
            <span>Cargando gráfica…</span>
          </div>
        )}
        {status === 'error' && (
          <div className="math-graph-error">
            <span>⚠ No se pudo cargar la gráfica</span>
            <small>Verifica tu conexión a internet</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default MathGraph;

